/**
 * 日期工具函数
 */

/**
 * 安全解析日期字符串为本地时间的 Date 对象
 * 避免 new Date('YYYY-MM-DD') 被解析为 UTC 导致的时区偏差问题
 * 将 'YYYY-MM-DD' 替换为 'YYYY/MM/DD' 后解析，确保所有浏览器一致解析为本地时间
 */
export function parseDateLocal(dateStr: string): Date {
  if (!dateStr) return new Date(NaN)
  // 将 YYYY-MM-DD 替换为 YYYY/MM/DD，确保一致解析为本地时间
  const normalized = dateStr.replace(/-/g, '/')
  return new Date(normalized)
}

/**
 * 判断字符串是否为 YYYY-MM-DD 格式
 */
function isYMDFormat(dateStr: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
}

/**
 * 安全格式化日期为 YYYY-MM-DD 字符串
 * 如果输入已经是 YYYY-MM-DD 格式，直接返回（避免 new Date 时区偏差）
 * @param date 日期对象、时间戳、或日期字符串
 */
export function formatDateToYMD(date: Date | number | string): string {
  if (typeof date === 'string') {
    // 如果已经是 YYYY-MM-DD 格式，直接返回
    if (isYMDFormat(date)) return date
    // 否则安全解析
    const d = parseDateLocal(date)
    if (Number.isNaN(d.getTime())) return date
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  const d = new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * 格式化日期为字符串
 * @param date 日期对象或时间戳
 * @param format 格式化模板，默认为 'YYYY-MM-DD HH:mm:ss'
 */
export function formatDate(date: Date | number | string, format: string = 'YYYY-MM-DD HH:mm:ss'): string {
  const d = new Date(date);

  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const seconds = d.getSeconds();

  const formatMap: { [key: string]: number } = {
    'YYYY': year,
    'MM': month,
    'DD': day,
    'HH': hours,
    'mm': minutes,
    'ss': seconds
  };

  let result = format;
  Object.entries(formatMap).forEach(([key, value]) => {
    result = result.replace(key, value.toString().padStart(2, '0'));
  });

  return result;
}

/**
 * 将字符串转换为日期对象（兼容旧版，但建议使用 parseDateLocal）
 * @param dateStr 日期字符串
 */
export function parseDate(dateStr: string): Date {
  return new Date(dateStr);
}

/**
 * 获取相对时间描述
 * @param date 要比较的日期
 * @param baseDate 基准日期，默认为当前时间
 */
export function getRelativeTime(date: Date | number | string, baseDate: Date | number | string = new Date()): string {
  const d1 = new Date(date);
  const d2 = new Date(baseDate);
  
  const diff = d2.getTime() - d1.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 365) {
    return `${Math.floor(days / 365)}年前`;
  }
  if (days > 30) {
    return `${Math.floor(days / 30)}个月前`;
  }
  if (days > 0) {
    return `${days}天前`;
  }
  if (hours > 0) {
    return `${hours}小时前`;
  }
  if (minutes > 0) {
    return `${minutes}分钟前`;
  }
  return '刚刚';
}
